import React, { useState, useEffect } from 'react';
import presecriptionIcon from "../../assest/icons/prescriptionIcon.png";
import CartIcon from "../../assest/icons/whiteCartIcon.png";
import renderStars from "../../utilities/renderProductStars";
import { apiDELETE, apiGET, apiPOST, apiPUT } from "../../utilities/apiHelpers";
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from "react-router-dom";
import { GoArrowRight } from "react-icons/go";
import { RiDeleteBinLine } from 'react-icons/ri';
import { setCartCount } from '../../redux/users/users';
import { API_URL } from '../../config';
import CustomLoader from '../Loader/CustomLoader';

// Explicit label -> backend field key. Do NOT derive key from label:
// admin saves some fields all-lowercase (moreInformationabout, howtoconsume),
// so camelCase derivation silently dropped those sections.
const menuItems = [
    { label: 'Introduction', key: 'introduction' },
    { label: 'Uses', key: 'uses' },
    { label: 'Therapeutic Effects', key: 'therapeuticEffects' },
    { label: 'Interaction', key: 'interaction' },
    { label: 'More Information About', key: 'moreInformationabout' },
    { label: 'How To Consume', key: 'howtoconsume' },
    { label: 'Safety Advice', key: 'safetyAdvices' },
    { label: 'Side Effects', key: 'sideEffects' },
    { label: 'Word of Advice', key: 'wordofAdvice' },
];

const ProductDetailsCard = ({ productInformations, productData, globalConfig, stepperProgressCartData, setStepperProgressCartData }) => {
    const [activeItem, setActiveItem] = useState('');
    const [selectedImage, setSelectedImage] = useState('');
    const [localAvailableQty, setLocalAvailableQty] = useState(productData?.productQuantity || 0);

    const dispatch = useDispatch();
    const { id } = useParams();
    const navigate = useNavigate();
    const isLoggedIn = useSelector((state) => state.user?.userData?.id) || false;

    const [incrementLoader, setIncrementLoader] = useState(false);
    const [decrementLoader, setDecrementLoader] = useState(false);
    const [deleteLoader, setDeleteLoader] = useState(false);
    const [addToCartloading, setAddToCartloading] = useState(false);

    useEffect(() => {
        setLocalAvailableQty(productData?.productQuantity || 0);
        if (productData?.bannerImg) {
            setSelectedImage(productData.bannerImg);
        } else if (productData?.images?.length > 0) {
            setSelectedImage(productData.images[0]);
        }
    }, [productData]);

    const isInStock = localAvailableQty > 0;

    const allThumbnails = [
        ...(productData?.bannerImg ? [productData.bannerImg] : []),
        ...(productData?.images || [])
    ].filter((img, index, self) => self.indexOf(img) === index);

    // --- UPDATED SCROLL LOGIC ---
    const scrollToSection = (sectionId) => {
        const hasIntroductionContent = productInformations?.introduction && String(productInformations.introduction).trim() !== "";

        // If clicking Introduction and content is empty, scroll to top of page
        if (sectionId === 'introduction' && !hasIntroductionContent) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setActiveItem(sectionId)
        } else {
            const section = document.getElementById(sectionId);
            if (section) {
                section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setActiveItem(sectionId)
            }
        }
    };

    const checkCartDataExists = (id) => stepperProgressCartData?.cartData?.some((item) => item.productDetails._id === id);
    const fetchCartQuantity = (id) => stepperProgressCartData?.cartData?.find((item) => item.productDetails._id === id)?.quantity || 0;
    const fetchCartId = (id) => stepperProgressCartData?.cartData?.find((item) => item.productDetails._id === id)?._id || 0;

    const triggerFullDataRefresh = async () => {
        try {
            const [stepperRes, productRes] = await Promise.all([
                apiGET(`/v1/stepper-progress/user-stepper-progress/${isLoggedIn}`),
                apiGET(`${API_URL}/v1/product/get-product/${id}`)
            ]);
            if (stepperRes?.data?.status) {
                setStepperProgressCartData(stepperRes.data.data);
                dispatch(setCartCount(stepperRes.data.data?.cartData?.length || 0));
            }
            const updatedProduct = productRes.data.data?.data?.product || productRes.data.data?.product;
            if (updatedProduct) setLocalAvailableQty(updatedProduct.productQuantity);
        } catch (error) { console.error(error); }
    };

    const handleIncrement = async (productId) => {
        const currentQuantity = fetchCartQuantity(productId);
        if (currentQuantity < localAvailableQty) {
            try {
                setIncrementLoader(true);
                const response = await apiPUT(`/v1/cart/update/${fetchCartId(productId)}`, { quantity: currentQuantity + 1 });
                if (response.status) await triggerFullDataRefresh();
            } finally { setIncrementLoader(false); }
        } else { toast.error('Stock limit reached'); }
    };

    const handleDecrement = async (productId) => {
        const currentQuantity = fetchCartQuantity(productId);
        if (currentQuantity > 1) {
            try {
                setDecrementLoader(true);
                const response = await apiPUT(`/v1/cart/update/${fetchCartId(productId)}`, { quantity: currentQuantity - 1 });
                if (response.status) await triggerFullDataRefresh();
            } finally { setDecrementLoader(false); }
        }
    };

    const addToCart = async (productId) => {
        if (!isLoggedIn) return toast.error("Please Login First");
        try {
            setAddToCartloading(true);
            const response = await apiPOST(`${API_URL}/v1/cart/add`, { productId, userId: isLoggedIn, quantity: 1 });
            if (response.status) {
                await triggerFullDataRefresh();
                toast.success("Added to cart");
            }
        } finally { setAddToCartloading(false); }
    };

    const handleRemoveCartItem = async (productId) => {
        try {
            setDeleteLoader(true);
            const response = await apiDELETE(`/v1/cart/delete/${fetchCartId(productId)}`);
            if (response.status) await triggerFullDataRefresh();
        } finally { setDeleteLoader(false); }
    };

    // --- FILTER MENU ITEMS BASED ON BACKEND CONTENT ---
    const sideBarLinks = menuItems.filter(({ label, key }) => {
        if (label === 'Safety Advice') {
            return productInformations?.safetyAdvices && Object.keys(productInformations.safetyAdvices).length > 0;
        }
        const content = productInformations?.[key];
        return content && String(content).trim() !== "";
    });

    // const finalMenuItems = sideBarLinks.length > 0 ? sideBarLinks : ['Introduction'];
    const finalMenuItems = sideBarLinks;

    return (
        <div className="max-w-[1440px] mx-full flex flex-col lg:flex-row gap-6">
            {finalMenuItems.length > 0 && (
                <aside className="w-full lg:w-72 flex-shrink-0">
                    <div className="sticky top-24 bg-white shadow-sm border border-gray-200 rounded-xl p-4 overflow-hidden">
                        <h3 className="text-lg font-bold mb-4 px-2">Quick Navigation</h3>
                        <ul className="space-y-1">
                            {finalMenuItems.map(({ label }, index) => {
                                const sectionId = label.replace(/\s+/g, '-').toLowerCase();
                                return (
                                    <li key={index}>
                                        <button
                                            onClick={() => scrollToSection(sectionId)}
                                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex justify-between items-center ${activeItem === sectionId ? 'bg-teal-50 text-[#14967F] font-semibold' : 'text-gray-600 hover:bg-gray-50'}`}
                                        >
                                            {label}
                                            <span className="opacity-50">›</span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </aside>
            )}

            <main className="flex-1 min-w-0">
                <div className="bg-white shadow-sm border border-gray-200 rounded-2xl overflow-hidden">
                    <div className="p-4 lg:p-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                            <div className="space-y-4">
                                <div className="aspect-square w-full max-w-[500px] mx-auto border border-gray-200 rounded-2xl flex items-center justify-center bg-white overflow-hidden shadow-inner">
                                    <img
                                        className="w-full h-full object-contain p-4 transition-all duration-500"
                                        src={selectedImage}
                                        alt={productData?.name}
                                    />
                                </div>
                                <div className={`flex gap-3 overflow-x-auto py-2 scrollbar-hide ${finalMenuItems.length === 0
                                        ? 'justify-center px-0' // Center thumbnails when no sidebar
                                        : 'px-4' // Normal left-aligned when sidebar exists
                                    }`}>
                                    {allThumbnails.map((image, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setSelectedImage(image)}
                                            className={`flex-shrink-0 w-16 h-16 lg:w-20 lg:h-20 rounded-lg border-2 transition-all overflow-hidden ${selectedImage === image
                                                    ? 'border-[#14967F] scale-95 shadow-md'
                                                    : 'border-gray-100 opacity-70 hover:opacity-100'
                                                }`}
                                        >
                                            <img src={image} className="w-full h-full object-cover" alt="thumb" />
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="flex flex-col">
                                <div className="flex justify-between items-start mb-2">
                                    {productData?.isPrescription && (
                                        <div className="bg-red-50 text-red-600 px-3 py-1 rounded-full text-[10px] font-bold flex items-center gap-1 uppercase tracking-wider">
                                            <img src={presecriptionIcon} className="h-3 w-3" alt="Rx" /> Prescription Required
                                        </div>
                                    )}
                                    <div className="flex">{renderStars(productData?.avgRating)}</div>
                                </div>

                                <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-4">{productData?.name}</h1>

                                <div className="space-y-3 mb-6 bg-gray-50 p-4 rounded-xl">
                                    <div className="flex text-sm"><span className="w-32 font-medium text-gray-500">Marketer</span><span className="text-gray-800">: {productData?.marketer}</span></div>
                                    <div className="flex text-sm"><span className="w-32 font-medium text-gray-500">Salt</span><span className="text-gray-800">: {productData?.saltComposition}</span></div>
                                    <div className="flex text-sm"><span className="w-32 font-medium text-gray-500">Origin</span><span className="text-gray-800">: {productData?.origin}</span></div>
                                    {productData?.brandId?.name && (
                                        <div className="flex text-sm"><span className="w-32 font-medium text-gray-500">Brand</span><span className="text-gray-800">: {productData.brandId.name}</span></div>
                                    )}
                                    {productData?.sku && productData.sku !== 'null' && (
                                        <div className="flex text-sm"><span className="w-32 font-medium text-gray-500">SKU</span><span className="text-gray-800">: {productData.sku}</span></div>
                                    )}
                                    {Array.isArray(productData?.categoryId) && productData.categoryId.length > 0 && (
                                        <div className="flex text-sm"><span className="w-32 font-medium text-gray-500">Categories</span><span className="text-gray-800">: {productData.categoryId.map(c => c?.name).filter(Boolean).join(', ')}</span></div>
                                    )}
                                </div>
                                <hr className='bg-gray-200' />

                                <div className="mb-6">
                                    <div className="flex items-baseline gap-3 mb-1">
                                        <span className="text-3xl font-bold text-gray-900">₹{productData?.discountedPrice}</span>
                                        <span className="text-lg text-gray-400 line-through">₹{productData?.price}</span>
                                        <span className="text-teal-600 font-bold text-sm">
                                            ({Math.round(((productData?.price - productData?.discountedPrice) / productData?.price) * 100)}% OFF)
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-500">{productData?.stripCapsuleQty} Units in one pack</p>
                                    <div className="mt-auto space-y-4">
                                        {isInStock ? (
                                            <div className="flex flex-col gap-3 mt-5 lg:mt-10">
                                                {checkCartDataExists(id) ? (
                                                    <div className="flex gap-3">
                                                        <div className="flex items-center bg-gray-100 rounded-xl p-1 h-12">
                                                            <button onClick={() => handleDecrement(id)} disabled={decrementLoader} className="w-10 h-10 flex items-center justify-center hover:bg-white rounded-lg transition-all">-</button>
                                                            <span className="px-4 font-bold min-w-[40px] text-center">{fetchCartQuantity(id)}</span>
                                                            <button onClick={() => handleIncrement(id)} disabled={incrementLoader} className="w-10 h-10 flex items-center justify-center hover:bg-white rounded-lg transition-all">+</button>
                                                        </div>
                                                        <button onClick={() => navigate('/view-cart')} className="flex-1 bg-[#14967F] text-white rounded-xl font-bold hover:bg-teal-700 flex items-center justify-center gap-2">
                                                            Go to Cart <GoArrowRight />
                                                        </button>
                                                        <button onClick={() => handleRemoveCartItem(id)} disabled={deleteLoader} className="w-12 h-12 flex items-center justify-center text-red-500 bg-red-50 rounded-xl hover:bg-red-100">
                                                            {deleteLoader ? <CustomLoader width='w-4' height='h-4' /> : <RiDeleteBinLine size={20} />}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button onClick={() => addToCart(id)} disabled={addToCartloading} className="w-full h-12 bg-[#14967F] text-white rounded-xl font-bold hover:shadow-lg hover:shadow-teal-100 transition-all flex items-center justify-center gap-2">
                                                        {addToCartloading ? <CustomLoader width='w-5' height='h-5' /> : <><img src={CartIcon} className="h-5 w-5" alt="" /> Add to Cart</>}
                                                    </button>
                                                )}
                                                <p className="text-orange-600 text-[10px] font-bold uppercase tracking-widest text-center">Only {localAvailableQty} units left in stock!</p>
                                            </div>
                                        ) : (
                                            <div className="w-full py-4 bg-gray-100 text-gray-500 rounded-xl font-bold text-center border-2 border-dashed mt-10">Out Of Stock</div>
                                        )}
                                    </div>
                                </div>


                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-8 space-y-6">
                    {finalMenuItems.map(({ label, key }) => {
                        const sectionId = label.replace(/\s+/g, '-').toLowerCase();
                        const content = productInformations?.[key];

                        // Skip rendering the section entirely if content is empty (except for Safety Advice which we check separately)
                        if (label !== 'Safety Advice' && (!content || String(content).trim() === "")) return null;

                        return (
                            <section id={sectionId} key={sectionId} className="bg-white border border-gray-100 rounded-2xl p-6 scroll-mt-28 hover:shadow-md transition-shadow">
                                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                                    <span className="w-1 h-6 bg-teal-500 rounded-full"></span>
                                    {label}
                                </h2>

                                {label === 'Safety Advice' ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {productInformations?.safetyAdvices && Object.entries(productInformations.safetyAdvices).map(([key, val], i) => (
                                            <div key={i} className="flex gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                                                <div className="w-10 h-10 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center font-bold flex-shrink-0 uppercase">{key.charAt(0)}</div>
                                                <div>
                                                    <div className="text-xs font-bold text-gray-800 uppercase mb-1">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                                                    <div className="text-sm text-gray-600 leading-relaxed">{val}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-gray-600 leading-relaxed text-sm lg:text-base whitespace-pre-line">
                                        {content}
                                    </div>
                                )}
                            </section>
                        );
                    })}
                </div>
            </main>
        </div>
    );
};

export default ProductDetailsCard;